import { addEdge, Background, Controls, MarkerType, ReactFlow, useReactFlow } from 'reactflow';
import { memo, useEffect, useContext, useMemo, useRef, useState, useCallback } from "react";
// import '@xyflow/react/dist/style.css';
import 'reactflow/dist/style.css';
import { WorkflowCanvasContext } from "../../contexts/WorkflowCanvasContextProvider";
import MainWorkflowNode from "./WorkflowNode";
import CustomEdge from "./CustomEdge";
// import WorkflowDrawer from "./WorkflowDrawer";
// import WorkflowConditionModal from "./ConditionNodeModal";
import { NOTE_EDGE_NODE_TYPE, NOTE_NODE_TYPE } from "../../helpers/Constant";
import { moveConditionToLast } from "../../helpers/Utils";
import NoteNode from "./NoteNode";
import NoteEdgeConnectionNode from "./NoteEdgeConnectionNode";
import StartNode from "./StartNode";
import { CHOICE_NODE_TYPE, START_NODE_COMPONENT } from "../../pages/action-journey-studio/common/const/node-types";
import Settings from "../../pages/action-journey-studio/settings";
import { DrawerPanel } from "../../pages/action-journey-studio/ui/drawer";
import { fetchWorkflowHistoryData, journeyGet, loadWorkflowHistoryData, undoWorkflowChange, redoWorkflowChange, getWorkflowHistoryStatus } from "../../services/model/journey-service";
import journeyStmStore from "../../pages/action-journey-studio-v2/common/service/journey-stm-store";
import { useAppInfo } from "../../helpers/hooks/common-hook";
import { useParams } from "react-router";
import { CgSpinner } from "react-icons/cg";
import { Alert } from "flowbite-react";
import { HiInformationCircle, HiClock, HiChevronDown, HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { BiUndo, BiRedo } from "react-icons/bi";
import { HiOutlineBookmarkSquare } from "react-icons/hi2";

import { timeFormatOptions } from "../../consts/common-consts";
import WorkflowConditionModal from "./ConditionNodeModal";
import { START } from "../../pages/action-journey-studio/common/const/settings-type";
import { createTimeBasedUniqueNumber } from "../../helpers/utils/object-util";
import { formatDistanceToNow } from "date-fns";
import EdgeDropMenu from "./EdgeDropMenu";

// Add shimmer effect styles to the document head
const addShimmerStyles = () => {
    if (!document.getElementById('shimmer-effect-style')) {
        const styleTag = document.createElement('style');
        styleTag.id = 'shimmer-effect-style';
        styleTag.innerHTML = `
            .shimmer-effect {
                position: relative;
                overflow: hidden;
                background: rgb(var(--raised));
                border-radius: 4px;
                box-shadow: 0 0 0 1px rgb(var(--line));
            }
            .shimmer-effect::after {
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                transform: translateX(-100%);
                background-image: linear-gradient(
                    90deg,
                    rgb(var(--panel) / 0) 0,
                    rgb(var(--panel) / 0.4) 20%,
                    rgb(var(--muted) / 0.5) 50%,
                    rgb(var(--panel) / 0.4) 80%,
                    rgb(var(--panel) / 0)
                );
                animation: shimmer 1.2s infinite ease-in-out;
                content: '';
                z-index: 1;
            }
            .shimmer-effect > * {
                opacity: 0.7;
                position: relative;
                z-index: 2;
            }
            @keyframes shimmer {
                100% {
                    transform: translateX(100%);
                }
            }
        `;
        document.head.appendChild(styleTag);
    }
};

const EdgeConditionDialog = ({ edgeId, initialCondition, onSave, onClose }) => {
    const [condition, setCondition] = useState(initialCondition || '');

    const handleSave = () => {
        onSave(edgeId, condition);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30" onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl border border-gray-200 w-[400px] p-4"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Edge Condition</h3>
                <textarea
                    autoFocus
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. if user asks for order cancellation"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                    rows={3}
                />
                <div className="flex justify-end gap-2 mt-3">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-3 py-1.5 text-xs text-accent-ink bg-primary rounded-md hover:bg-secondary transition-colors"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

// Auto save preference is remembered across sessions.
const AUTO_SAVE_STORAGE_KEY = 'botcircuits-auto-save-preference';

// Save progress indicator. Rendered inline in the document controls, taking the
// Save button's place while a save is in flight, so it never overlaps the title.
const BackgroundSyncLoader = () => (
    <span
        role="status"
        aria-live="polite"
        className="inline-flex h-9 items-center gap-2 rounded-xl bg-gray-100 px-3.5 text-sm font-medium text-gray-600"
    >
        {/* Ring spinner: a neutral track with one lit arc reads calmer than a full spinning glyph. */}
        <span
            className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
            aria-hidden="true"
        />
        Saving
    </span>
);

const WorkflowCanvas = ({ journeyId, currentJourneySession, currentJourneyConfig }) => {

    const { appId } = useAppInfo();

    const reactFlowInstance = useReactFlow();

    const workflowCanvasContext = useContext(WorkflowCanvasContext);

    const reactFlowWrapper = useRef(null);

    const backgroundSyncActive = workflowCanvasContext.backgroundSyncActive;
    const saveSuccess = workflowCanvasContext.saveSuccess;
    const saveError = workflowCanvasContext.saveError;
    const setSaveError = workflowCanvasContext.setSaveError;
    const isPreview = workflowCanvasContext.isPreview;

    const initialFitViewDone = useRef(false);

    const [lastModifiedTime, setLastModifiedTime] = useState();
    const [workflowHistory, setWorkflowHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
    const [isProcessingWFHistory, setIsProcessingWFHistory] = useState(false);
    const [undoRedoStatus, setUndoRedoStatus] = useState({ canUndo: false, canRedo: false });
    const [edgeDropMenu, setEdgeDropMenu] = useState(null);

    const [dataLoadingState, setDataLoading] = useState({
        processing: false,
        success: false,
        failed: false,
        message: null,
    });

    const updateDataLoadingState = (processing, success, failed, message) => {
        setDataLoading(Object.assign({}, { processing, success, failed, message }));
    };

    const updateModifiedTime = (modifiedTime) => {
        const time = modifiedTime ? new Date(modifiedTime) : new Date();
        // const readableDate = new Intl.DateTimeFormat('en-US', timeFormatOptions).format(time);
        const readableDate = formatDistanceToNow(time, { addSuffix: true })
        setLastModifiedTime(readableDate);
    }

    const fetchWorkflowHistory = async () => {
        if (!appId || !journeyId) return;

        try {
            setIsLoadingHistory(true);
            const response = await fetchWorkflowHistoryData(appId, journeyId)
            console.log('Workflow history:', response);
            setWorkflowHistory(response);
            await fetchUndoRedoStatus();
        } catch (error) {
            console.error('Error fetching workflow history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const fetchUndoRedoStatus = async () => {
        if (!appId || !journeyId) return;

        try {
            const status = await getWorkflowHistoryStatus(appId, journeyId);
            console.log('Undo/Redo status:', status);
            setUndoRedoStatus(status);
        } catch (error) {
            console.error('Error fetching undo/redo status:', error);
            setUndoRedoStatus({ canUndo: false, canRedo: false });
        }
    };

    const handleManualSave = () => {
        if (workflowCanvasContext?.saveWorkflow) {
            workflowCanvasContext.saveWorkflow(false);
        }
    };

    const handleAutoSaveChange = (e) => {
        const isChecked = e.target.checked;
        try {
            localStorage.setItem(AUTO_SAVE_STORAGE_KEY, JSON.stringify(isChecked));
        } catch (error) {
            console.error('Error saving auto save preference:', error);
        }
        if (workflowCanvasContext?.setAutoSave) {
            workflowCanvasContext.setAutoSave(isChecked);
        }
    };

    // Restore the saved auto save preference once, on mount.
    useEffect(() => {
        try {
            const saved = localStorage.getItem(AUTO_SAVE_STORAGE_KEY);
            if (saved !== null && workflowCanvasContext?.setAutoSave) {
                workflowCanvasContext.setAutoSave(JSON.parse(saved));
            }
        } catch (error) {
            console.error('Error loading auto save preference:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUndo = async () => {
        if (!appId || !journeyId) return;

        try {
            setIsProcessingWFHistory(true);
            updateDataLoadingState(true, false, false, "Undoing last change...");

            const response = await undoWorkflowChange(appId, journeyId);

            if (!response) {
                throw new Error('Failed to undo last change');
            }

            updateDataLoadingState(false, true, false);
            await fetchJourneyData();
            await fetchWorkflowHistory();
            await fetchUndoRedoStatus();

        } catch (error) {
            console.error('Error undoing last change:', error);
            updateDataLoadingState(false, false, true, "Failed to undo last change");
        } finally {
            setIsProcessingWFHistory(false);
        }
    };

    const handleRedo = async () => {
        if (!appId || !journeyId) return;

        try {
            setIsProcessingWFHistory(true);
            updateDataLoadingState(true, false, false, "Redoing last change...");

            const response = await redoWorkflowChange(appId, journeyId);

            if (!response) {
                throw new Error('Failed to redo last change');
            }

            updateDataLoadingState(false, true, false);
            await fetchJourneyData();
            await fetchWorkflowHistory();
            await fetchUndoRedoStatus();

        } catch (error) {
            console.error('Error redoing last change:', error);
            updateDataLoadingState(false, false, true, "Failed to redo last change");
        } finally {
            setIsProcessingWFHistory(false);
        }
    };

    const loadHistoryVersion = async (historyItem) => {
        if (!appId || !journeyId || !historyItem) return;

        try {
            updateDataLoadingState(true, false, false, "Loading workflow history version...");
            setSelectedHistoryItem(historyItem);

            const response = await loadWorkflowHistoryData(appId, journeyId, historyItem.historyId);

            if (!response) {
                throw new Error('Failed to load workflow history version');
            }

            updateDataLoadingState(false, true, false);

            await fetchJourneyData()

        } catch (error) {
            console.error('Error loading workflow history version:', error);
            updateDataLoadingState(false, false, true, "Failed to load workflow history version");
        }
    };

    const fetchJourneyData = async () => {
        try {
            updateDataLoadingState(true, false, false);
            let savedJourney
            if (currentJourneyConfig) {
                savedJourney = currentJourneyConfig
            } else {
                savedJourney = await journeyGet(appId, journeyId);
            }

            const { modifiedAt } = savedJourney;
            if (modifiedAt) {
                updateModifiedTime(modifiedAt);
            }

            workflowCanvasContext.setMetadata({
                appId,
                journeyId,
                slots: savedJourney.slots || {}
            })

            if (
                savedJourney?.stm?.metadata?.nodes
            ) {
                const seenIds = new Set();
                const loadedNodes = (savedJourney?.stm?.metadata?.nodes || [])
                    .filter(n => {
                        if (seenIds.has(n.id)) return false;
                        seenIds.add(n.id);
                        return true;
                    })
                    .map((n) => {
                        if (n.type === 'MainWorkflowNode') {
                            const { dragHandle, ...rest } = n;
                            return rest;
                        }
                        return n;
                    });
                workflowCanvasContext.setNodes(loadedNodes);
                workflowCanvasContext.setEdges(savedJourney?.stm?.metadata?.edges);
            } else {
                const id = createTimeBasedUniqueNumber()
                workflowCanvasContext.setNodes([
                    {
                        id,
                        position: { x: 0, y: 0 },
                        type: START_NODE_COMPONENT,
                        data: {
                            stateConfig: {
                                stateType: START
                            }
                        },
                    }
                ]);
            }
            updateDataLoadingState(false, true, false);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (journeyId === undefined) return;
        fetchJourneyData();
        fetchWorkflowHistory();
        fetchUndoRedoStatus();
        if (workflowCanvasContext) {
            workflowCanvasContext.setWorkflowHistoryLoader(() => fetchWorkflowHistory)
        }
        // Add shimmer effect styles
        addShimmerStyles();
    }, [journeyId, appId]);

    useEffect(() => {
        if (currentJourneyConfig === undefined) return;
        fetchJourneyData();
    }, [currentJourneyConfig]);

    // Check for last workflow step from agent preview when component mounts
    useEffect(() => {
        if (reactFlowInstance && !isPreview) {
            const lastWorkflowStep = localStorage.getItem('lastWorkflowStep');
            const lastJourneyId = localStorage.getItem('lastJourneyId');

            console.log("Checking for last workflow step:", lastWorkflowStep, "Last journey ID:", lastJourneyId, "Current journey ID:", journeyId);

            if (lastWorkflowStep && lastJourneyId === journeyId) {
                // Find the node to highlight
                const targetNode = reactFlowInstance.getNode(lastWorkflowStep);

                if (targetNode) {
                    console.log("Found target node to highlight:", targetNode.id);

                    // Clear the localStorage after using it
                    localStorage.removeItem('lastWorkflowStep');
                    localStorage.removeItem('lastJourneyId');

                    // Reset the current view before focusing on the node
                    reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });

                    // Use a more direct approach to highlight the node
                    // First, find the DOM element of the node
                    setTimeout(() => {
                        const nodeElement = document.querySelector(`[data-id="${lastWorkflowStep}"]`);
                        if (nodeElement) {
                            console.log("Found DOM element for node:", nodeElement);

                            // Add a highlight class to the node element
                            nodeElement.classList.add('highlight-node');

                            // Create and apply a style tag for the highlight effect if it doesn't exist
                            if (!document.getElementById('node-highlight-style')) {
                                const styleTag = document.createElement('style');
                                styleTag.id = 'node-highlight-style';
                                styleTag.innerHTML = `
                                    .highlight-node {
                                        animation: pulse-highlight 1.5s infinite alternate;
                                        box-shadow: 0 0 0 8px rgba(250, 204, 21, 0.7) !important;
                                    }
                                    @keyframes pulse-highlight {
                                        0% {
                                            box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.7);
                                        }
                                        100% {
                                            box-shadow: 0 0 0 12px rgba(250, 204, 21, 0.7);
                                        }
                                    }
                                `;
                                document.head.appendChild(styleTag);
                            }

                            // Zoom into the node using fitView
                            reactFlowInstance.fitView({
                                duration: 800,
                                nodes: [targetNode],
                                padding: 0.8,
                                minZoom: 0.2,
                                maxZoom: 0.7,
                            });

                            // Remove highlight after a delay
                            setTimeout(() => {
                                nodeElement.classList.remove('highlight-node');
                            }, 10000);
                        }
                    }, 500); // Give the DOM time to render
                }
            }
        }
    }, [reactFlowInstance, isPreview, journeyId]);

    // Fit view on initial load
    useEffect(() => {
        if (
            reactFlowInstance &&
            workflowCanvasContext.nodes.length > 0 &&
            !initialFitViewDone.current &&
            !isPreview &&
            dataLoadingState.success
        ) {
            const lastWorkflowStep = localStorage.getItem('lastWorkflowStep');
            const lastJourneyId = localStorage.getItem('lastJourneyId');

            // Only fit view if we're not restoring a specific node view
            if (!lastWorkflowStep || lastJourneyId !== journeyId) {
                // Small delay to ensure nodes are rendered
                setTimeout(() => {
                    reactFlowInstance.fitView({ padding: 0.2 });
                    initialFitViewDone.current = true;
                }, 100);
            }
        }
    }, [reactFlowInstance, workflowCanvasContext.nodes, isPreview, journeyId, dataLoadingState.success]);

    useEffect(() => {
        if (saveSuccess) {
            updateModifiedTime()
        }
    }, [saveSuccess]);

    useEffect(() => {
        if (!reactFlowInstance) return;
        const invalids = workflowCanvasContext.invalidStates;
        if (!invalids || invalids.length === 0) return;
        const invalidId = String(invalids[0]);
        let targetNode = reactFlowInstance.getNode(invalidId);
        if (!targetNode) {
            const parent = workflowCanvasContext.nodes?.find(n => n?.data?.children?.some(ch => String(ch.id) === invalidId));
            if (parent) targetNode = parent;
        }
        if (targetNode) {
            setTimeout(() => {
                try {
                    reactFlowInstance.fitView({
                        nodes: [targetNode],
                        padding: 0.8,
                        duration: 800,
                        minZoom: 0.2,
                        maxZoom: 0.7,
                    });
                } catch (e) {
                }
            }, 150);
        }
    }, [workflowCanvasContext.invalidStates, workflowCanvasContext.nodes, reactFlowInstance]);

    useEffect(() => {
        if (currentJourneyConfig) {
            console.log('currentJourneyConfig')
        }
    }, [currentJourneyConfig]);

    useEffect(() => {
        if (
            reactFlowInstance &&
            currentJourneySession &&
            currentJourneySession.runningBlock
        ) {

            const targetNode = reactFlowInstance.getNode(
                currentJourneySession.runningBlock
            );

            console.log("found currentJourneyConfig", currentJourneyConfig);
            console.log("found targetNode", targetNode);
            if (targetNode) {
                // Reset the current view before focusing on the new node
                reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });

                // Zoom into the node using fitView
                reactFlowInstance.fitView({
                    duration: 800,
                    nodes: [targetNode],
                    padding: 0.8, // Adjust the padding to control how much the view is zoomed
                    minZoom: 0.2, // Slight zoom for better focus
                    maxZoom: 0.7, // Optional: limit the maximum zoom level
                });
            }
        }
    }, [currentJourneySession, reactFlowInstance, workflowCanvasContext.nodes]);

    const nodeTypes = useMemo(() => ({
        StartNode,
        MainWorkflowNode,
        NoteNode,
        NoteEdgeConnectionNode,
    }), []);

    const edgeTypes = useMemo(() => ({
        custom: CustomEdge,
    }), []);

    const handleConnectStart = useCallback((event, { nodeId, handleId }) => {
        workflowCanvasContext.setConnectingSource({
            nodeId,
            handleId
        })
    }, [])

    const handleNodeMouseEnter = (event, node) => {
        workflowCanvasContext.setConnectingTarget(node);
    }

    const handleNodeMouseLeave = (event, node) => {
        // Clear target when mouse leaves a node so releasing on empty space works correctly
        if (workflowCanvasContext.connectingTarget?.id === node.id) {
            workflowCanvasContext.setConnectingTarget(null);
        }
    }

    const handleConnectEnd = (event) => {
        console.log('handleConnectEnd connectingSource', workflowCanvasContext.connectingSource);
        console.log('handleConnectEnd connectingTarget', workflowCanvasContext.connectingTarget);
        if (workflowCanvasContext.connectingSource && workflowCanvasContext.connectingTarget) {
            const sourceId = workflowCanvasContext.connectingSource.nodeId
            const targetId = workflowCanvasContext.connectingTarget.id
            const newEdge = {
                id: `edge-${sourceId}-${targetId}`,
                source: sourceId,
                target: targetId,
                sourceHandle: workflowCanvasContext.connectingSource.handleId,
                targetHandle: `target-${targetId}-left-top`,
                type: 'custom',
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#969b9f',
                },
                style: {
                    strokeWidth: 2,
                    stroke: '#969b9f',
                },
            };

            // Allow multiple output edges from any node type
            workflowCanvasContext.setEdges((eds) => addEdge(newEdge, eds));

            workflowCanvasContext.setConnectingSource(null)
            workflowCanvasContext.setConnectingTarget(null);
        } else if (workflowCanvasContext.connectingSource && !workflowCanvasContext.connectingTarget) {
            // Edge was dropped on empty canvas space — show context menu
            const clientX = event.changedTouches ? event.changedTouches[0].clientX : event.clientX;
            const clientY = event.changedTouches ? event.changedTouches[0].clientY : event.clientY;

            setEdgeDropMenu({
                x: clientX,
                y: clientY,
                sourceNodeId: workflowCanvasContext.connectingSource.nodeId,
                sourceHandleId: workflowCanvasContext.connectingSource.handleId,
            });
            // Don't reset connectingSource yet — we need it when the user picks from the menu
        } else {
            workflowCanvasContext.setConnectingSource(null)
            workflowCanvasContext.setConnectingTarget(null);
        }
    }

    const handleEdgeDropMenuSelect = (nodeConfig) => {
        if (!edgeDropMenu) return;

        const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
        const position = reactFlowInstance.project({
            x: edgeDropMenu.x - reactFlowBounds.left,
            y: edgeDropMenu.y - reactFlowBounds.top,
        });

        const newNodeId = createTimeBasedUniqueNumber();
        const nodeChildId = +createTimeBasedUniqueNumber();

        let newNode;

        if (nodeConfig.nodeType === CHOICE_NODE_TYPE) {
            newNode = {
                id: newNodeId,
                position,
                data: {
                    label: `New Block`,
                    children: [
                        {
                            id: nodeChildId,
                            label: nodeConfig.label,
                            nodeType: nodeConfig.nodeType,
                            stateConfig: nodeConfig.stateConfig || { choices: [] },
                        },
                    ]
                },
                type: "MainWorkflowNode",
                style: {
                    borderRadius: "12px",
                    width: "300px",
                },
            };
        } else {
            newNode = {
                id: newNodeId,
                position,
                data: {
                    label: `New Block`,
                    children: [
                        {
                            id: nodeChildId,
                            label: nodeConfig.label,
                            nodeType: nodeConfig.nodeType,
                            stateConfig: nodeConfig.stateConfig,
                        },
                    ]
                },
                type: "MainWorkflowNode",
                style: {
                    borderRadius: "12px",
                    width: "300px",
                },
            };
        }

        // Create the edge from source to the new node
        const sourceId = edgeDropMenu.sourceNodeId;
        const newEdge = {
            id: `edge-${sourceId}-${newNodeId}`,
            source: sourceId,
            target: newNodeId,
            sourceHandle: edgeDropMenu.sourceHandleId,
            targetHandle: `target-${newNodeId}-left-top`,
            type: 'custom',
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: '#969b9f',
            },
            style: {
                strokeWidth: 2,
                stroke: '#969b9f',
            },
        };

        // Add the new node
        workflowCanvasContext.setNodes((prev) => [...prev, newNode]);

        // Add the edge - allow multiple output edges from any node type
        workflowCanvasContext.setEdges((eds) => addEdge(newEdge, eds));

        // Cleanup
        setEdgeDropMenu(null);
        workflowCanvasContext.setConnectingSource(null);
        workflowCanvasContext.setConnectingTarget(null);
    };

    const handleEdgeDropMenuClose = () => {
        setEdgeDropMenu(null);
        workflowCanvasContext.setConnectingSource(null);
        workflowCanvasContext.setConnectingTarget(null);
    }

    const onDrop = (event) => {

        event.preventDefault();

        const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();

        // Calculate the correct position in the ReactFlow coordinate system
        const position = reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
        });

        const nodeAtPosition = workflowCanvasContext.nodes.find(node => {
            const nodeX = node.position.x;
            const nodeY = node.position.y;
            const nodeWidth = node.width || 300; // Default width if not specified
            const nodeHeight = node.height || 100; // Default height if not specified

            return (
                position.x > nodeX &&
                position.x < nodeX + nodeWidth &&
                position.y > nodeY &&
                position.y < nodeY + nodeHeight
            );
        })

        if (nodeAtPosition) {
            return
        } else {
            if (event.dataTransfer.getData('nodeData')) {

                const parseNodeData = JSON.parse(event.dataTransfer.getData('nodeData'));

                console.log('parseNodeData', parseNodeData)

                let previousData = workflowCanvasContext.nodes;
                let findNode;

                if (parseNodeData.nodeType === NOTE_NODE_TYPE || parseNodeData.nodeType === NOTE_EDGE_NODE_TYPE) {
                    const noteNode = {
                        id: createTimeBasedUniqueNumber(),
                        position, // Use the calculated position
                        type: parseNodeData.nodeType === NOTE_NODE_TYPE ? "NoteNode" : "NoteEdgeConnectionNode",
                        data: {
                            txt: "",
                            nodeType: parseNodeData.nodeType
                        },
                    }

                    workflowCanvasContext.setNodes(prev => [...prev, noteNode]);

                    return;
                }

                if (parseNodeData.blockId) {

                    const _nodes = workflowCanvasContext.nodes.map(node => {
                        if (node.id === parseNodeData.blockId) {

                            const updatedNodes = node.data.children.filter(item => item.id !== parseNodeData.childrenId);
                            findNode = node.data.children.find(item => item.id === parseNodeData.childrenId);

                            if (updatedNodes.length > 0) {
                                return (
                                    {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            children: updatedNodes,
                                        }
                                    }
                                )
                            }
                            return undefined;
                        }
                        return node;
                    }).filter(node => node !== undefined);

                    previousData = _nodes;
                }

                const newNodeId = createTimeBasedUniqueNumber();
                const nodeChildId = +createTimeBasedUniqueNumber();

                let newNode;

                if (parseNodeData.nodeType === CHOICE_NODE_TYPE) {

                    if (findNode) {
                        newNode = {
                            id: newNodeId,
                            position, // Use the calculated position
                            data: {
                                label: `New Block`,
                                children: [findNode]
                            },
                            type: "MainWorkflowNode",
                            style: {
                                borderRadius: "12px",
                                width: "300px",
                                // backgroundColor: '#FFFFFF',
                            },
                        }
                    } else {
                        newNode = {
                            id: newNodeId,
                            position, // Use the calculated position
                            data: {
                                label: `New Block`,
                                children: [
                                    {
                                        id: nodeChildId,
                                        label: parseNodeData.label,
                                        nodeType: parseNodeData.nodeType,
                                        stateConfig: {
                                            choices: []
                                        }
                                    },
                                ]
                            },
                            type: "MainWorkflowNode",
                            style: {
                                borderRadius: "12px",
                                width: "300px",
                                // backgroundColor: '#FFFFFF',
                            },
                        }
                    }
                } else {

                    if (findNode) {
                        newNode = {
                            id: newNodeId,
                            position, // Use the calculated position
                            data: {
                                label: `New Block`,
                                children: [findNode]
                            },
                            type: "MainWorkflowNode",
                            style: {
                                borderRadius: "12px",
                                width: "300px",
                                // backgroundColor: '#FFFFFF',
                            },
                        }
                    } else {
                        newNode = {
                            id: newNodeId,
                            position, // Use the calculated position
                            data: {
                                label: `New Block`,
                                children: [
                                    {
                                        id: nodeChildId,
                                        label: parseNodeData.label,
                                        nodeType: parseNodeData.nodeType,
                                        stateConfig: parseNodeData.stateConfig
                                    },
                                ]
                            },
                            type: "MainWorkflowNode",
                            style: {
                                borderRadius: "12px",
                                width: "300px",
                                // backgroundColor: '#FFFFFF',
                            },
                        }
                    }
                }

                console.log('WC previousData', previousData)
                console.log('WC newNode', newNode)
                workflowCanvasContext.setNodes([...previousData, newNode]);

            }
        }

    };

    const [dragStartPos, setDragStartPos] = useState(null);
    const { getNodes } = useReactFlow();

    const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 0.1 });
    useEffect(() => {
        // Center align viewport based on container size and initial zoom level
        const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
        const centerX = width / 2;
        const centerY = height / 2;

        setViewport({
            x: centerX - (width * viewport.zoom) / 2,
            y: centerY - (height * viewport.zoom) / 2,
            zoom: 0.1,
        });
    }, []);

    const onNodeDragStart = (event, node) => {
        // Capture the initial position when dragging starts
        setDragStartPos({ x: node.position.x, y: node.position.y });
    };

    const checkCollision = (draggedNode, targetNode) => {
        const draggedRect = {
            x: draggedNode.position.x,
            y: draggedNode.position.y,
            width: draggedNode.width || 100, // assuming width if not defined
            height: draggedNode.height || 50 // assuming height if not defined
        };

        const targetRect = {
            x: targetNode.position.x,
            y: targetNode.position.y,
            width: targetNode.width || 100, // assuming width if not defined
            height: targetNode.height || 50 // assuming height if not defined
        };

        return !(
            draggedRect.x > targetRect.x + targetRect.width ||
            draggedRect.x + draggedRect.width < targetRect.x ||
            draggedRect.y > targetRect.y + targetRect.height ||
            draggedRect.y + draggedRect.height < targetRect.y
        );
    };

    const onNodeDragStop = (event, draggedNode) => {

        if (draggedNode.data.nodeType === NOTE_EDGE_NODE_TYPE || draggedNode.data.nodeType === NOTE_NODE_TYPE) {
            return;
        }

        const hasMoved = dragStartPos && (dragStartPos.x !== draggedNode.position.x || dragStartPos.y !== draggedNode.position.y);

        // NOT ALLOW TO DRAG BLOCK INTO ANOTHER
        // if (hasMoved) {
        //     // Get all nodes in the React Flow graph
        //     const allNodes = getNodes();

        //     // Check for collisions with other nodes
        //     allNodes.forEach((targetNode) => {
        //         if (targetNode.id !== draggedNode.id) {
        //             if (checkCollision(draggedNode, targetNode)) {
        //                 // Your custom logic when a block is dropped on another node (targetNode)
        //                 const targetBlock = workflowCanvasContext.nodes.find((node) => node.id === targetNode.id);
        //                 const draggedBlock = workflowCanvasContext.nodes.find((node) => node.id === draggedNode.id);

        //                 if (targetBlock.data.nodeType !== NOTE_EDGE_NODE_TYPE && targetBlock.data.nodeType !== NOTE_NODE_TYPE) {

        //                     let isTargetConditional;
        //                     targetBlock.data.children.forEach(item => {
        //                         if (item.nodeType === CHOICE_NODE_TYPE) {
        //                             isTargetConditional = true;
        //                         }
        //                     })
        //                     let isDraggedConditional;
        //                     draggedBlock.data.children.forEach(item => {
        //                         if (item.nodeType === CHOICE_NODE_TYPE) {
        //                             isDraggedConditional = true;
        //                         }
        //                     })
        //                     if (isTargetConditional === true && isDraggedConditional === true) {
        //                     } else {
        //                         targetBlock.data.children = moveConditionToLast([...draggedBlock.data.children, ...targetBlock.data.children]);
        //                         const newNodes = workflowCanvasContext.nodes.filter((node) => node.id !== draggedNode.id && node.id !== targetNode.id);
        //                         workflowCanvasContext.setNodes([...newNodes, targetBlock]);
        //                     }
        //                 }

        //             }
        //         }
        //     });
        // }

        // Reset the drag start position after the drag ends
        setDragStartPos(null);
        workflowCanvasContext.onEdgeUpdateForMove(event, draggedNode)
    };


    const onDrawerClose = () => {

        workflowCanvasContext.closeNodeDrawer();

        if (workflowCanvasContext.nodeAttribute.nodeType !== CHOICE_NODE_TYPE) {

            const contentData = { ...workflowCanvasContext.nodeAttribute };
            delete contentData.parentId;
            delete contentData.id;

            const selectedBlock = workflowCanvasContext.nodes
                .find(item => item.id === workflowCanvasContext.nodeAttribute.parentId);

            selectedBlock.data.children = selectedBlock.data.children.map(item => {
                if (item.id === workflowCanvasContext.nodeAttribute.id) {
                    return {
                        ...item,
                        stateConfig: contentData.stateConfig
                    }
                }
                return item;
            });

            const findIndex = workflowCanvasContext.nodes
                .findIndex(item => item.id === workflowCanvasContext.nodeAttribute.parentId);

            workflowCanvasContext.nodes[findIndex] = selectedBlock;

            workflowCanvasContext.setNodes([...workflowCanvasContext.nodes]);
        }

    }

    if (dataLoadingState.processing) {
        return (
            <div className="flex flex-grow items-center justify-center p-3">
                <div className="flex items-center gap-1 text-gray-500">
                    <CgSpinner size={24} className="animate-spin" />
                    Loading..
                </div>
            </div>
        );
    }

    if (dataLoadingState.failed) {
        return (
            <Alert color="failure" icon={HiInformationCircle}>
                <span className="font-medium">Error!</span> We're sorry, but we couldn't
                load the selected journey.
            </Alert>
        );
    }

    const onEdgeUpdateStart = () => {
        console.log('onEdgeUpdateStart')
    }

    const onEdgeUpdate = () => {
        console.log('onEdgeUpdate')
    }

    return (
        <>

            {/* Error Alert */}
            {/* {(!isPreview && saveError) && (
                <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-50 w-96">
                    <Alert color="failure" icon={HiInformationCircle} onDismiss={() => setSaveError(null)}>
                        <div>
                            <span className="font-medium">Workflow Save Failed!</span>
                            <div className="mt-2 text-sm whitespace-pre-line">{saveError}</div>
                        </div>
                    </Alert>
                </div>
            )} */}

            {(!isPreview && lastModifiedTime && !backgroundSyncActive) && (
                <div className="absolute right-8 bottom-4 z-10 flex items-center gap-2">
                    <small className="text-gray-500">Last modified {lastModifiedTime}</small>

                </div>
            )}

            {/* Document controls: save / undo / redo / auto save, grouped as one toolbar. */}
            {!isPreview && (
                <div className="absolute right-4 top-0 z-10 flex items-center py-2.5">
                    <div className="flex h-[52px] items-center gap-2 py-1">
                        {/* Undo / redo are only meaningful while the user drives saving manually. */}
                        {!workflowCanvasContext.autoSaveEnabled && (
                            <>
                                <button
                                    type="button"
                                    title="Undo"
                                    aria-label="Undo"
                                    onClick={handleUndo}
                                    disabled={!undoRedoStatus.canUndo || isProcessingWFHistory || isLoadingHistory}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150
                                        hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:pointer-events-none
                                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                                           ${isProcessingWFHistory ? "shimmer-effect" : ""}`}
                                >
                                    <BiUndo className="h-[18px] w-[18px]" />
                                </button>

                                <button
                                    type="button"
                                    title="Redo"
                                    aria-label="Redo"
                                    onClick={handleRedo}
                                    disabled={!undoRedoStatus.canRedo || isProcessingWFHistory || isLoadingHistory}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150
                                        hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:pointer-events-none
                                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                                           ${isProcessingWFHistory ? "shimmer-effect" : ""}`}
                                >
                                    <BiRedo className="h-[18px] w-[18px]" />
                                </button>

                            </>
                        )}

                        {/* Auto save */}
                        {/* <label className="flex h-8 cursor-pointer items-center gap-2">
                            <span className="relative inline-flex items-center">
                                <input
                                    id="workflow-auto-save"
                                    type="checkbox"
                                    className="peer sr-only"
                                    checked={workflowCanvasContext.autoSaveEnabled}
                                    onChange={handleAutoSaveChange}
                                />
                                <span
                                    className="relative h-5 w-9 shrink-0 rounded-full bg-gray-200 transition-colors duration-200
                                        after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white
                                        after:shadow-sm after:transition-transform after:duration-200 after:content-['']
                                        peer-checked:bg-brand-lime peer-checked:after:translate-x-4
                                        peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1"
                                />
                            </span>
                            <span className="whitespace-nowrap text-xs font-medium leading-none text-gray-600">
                                Auto save
                            </span>
                        </label> */}

                        {/* While a save runs the button gives way to the progress pill. */}
                        {backgroundSyncActive && <BackgroundSyncLoader />}

                        {/* Manual save is redundant while auto save is on. */}
                        {(!workflowCanvasContext.autoSaveEnabled && !backgroundSyncActive) && (
                            <button
                                type="button"
                                title="Save workflow"
                                onClick={handleManualSave}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-primary px-3.5 text-sm font-medium text-accent-ink shadow-sm transition-all duration-200 hover:bg-secondary hover:shadow-md active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                            >
                                <HiOutlineBookmarkSquare className="h-4 w-4" />
                                Save
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/*<p className="">{workflowCanvasContext.nodes.length}</p>*/}

            {!isPreview &&
                <DrawerPanel
                    key={journeyId}
                    open={workflowCanvasContext.nodeDrawerVisible}
                    onClose={() => onDrawerClose()}
                >
                    <Settings
                        onUpdate={() => { }}
                        closeSettingPage={() => { }}
                    />
                </DrawerPanel>
            }

            <div className="flex-grow h-full" ref={reactFlowWrapper}>
                {isPreview ?
                    <ReactFlow
                        proOptions={{ hideAttribution: true }}
                        nodes={workflowCanvasContext.nodes}
                        edges={workflowCanvasContext.edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        fitView={true}
                        snapToGrid={true}
                    >
                        <Background />

                        <Controls
                            style={{ display: "flex", flexDirection: "row" }}
                        />

                    </ReactFlow>
                    :
                    <ReactFlow
                        proOptions={{ hideAttribution: true }}
                        nodes={workflowCanvasContext.nodes}
                        edges={workflowCanvasContext.edges}
                        onNodesChange={workflowCanvasContext.onNodesChange}
                        onEdgesChange={workflowCanvasContext.onEdgesChange}
                        onConnect={workflowCanvasContext.onConnect}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onDrop={onDrop}
                        // onDrop={workflowCanvasContext.onDrop}
                        onConnectStart={handleConnectStart}
                        onNodeMouseEnter={handleNodeMouseEnter}
                        onNodeMouseLeave={handleNodeMouseLeave}
                        onConnectEnd={handleConnectEnd}
                        onNodeDragStart={onNodeDragStart} // Track when dragging starts
                        onNodeDragStop={onNodeDragStop}
                        onDragOver={workflowCanvasContext.onDragOver}
                        onEdgeUpdateStart={onEdgeUpdateStart}
                        onEdgeUpdate={onEdgeUpdate}
                        // fitView={true}
                        // style={{width: "100%", height: "100%"}}
                        defaultViewport={viewport} // Setting zoom close to 0
                        minZoom={0.02}
                        maxZoom={2}
                    >
                        <Background />

                        <Controls
                            style={{ display: "flex", flexDirection: "row" }}
                        />

                    </ReactFlow>
                }


            </div>

            <WorkflowConditionModal />

            {/* Edge Condition Prompt Dialog */}
            {workflowCanvasContext.edgeConditionModal?.visible && (
                <EdgeConditionDialog
                    edgeId={workflowCanvasContext.edgeConditionModal.edgeId}
                    initialCondition={workflowCanvasContext.edgeConditionModal.condition}
                    onSave={workflowCanvasContext.saveEdgeCondition}
                    onClose={workflowCanvasContext.closeEdgeConditionModal}
                />
            )}

            {edgeDropMenu && (
                <EdgeDropMenu
                    x={edgeDropMenu.x}
                    y={edgeDropMenu.y}
                    onSelect={handleEdgeDropMenuSelect}
                    onClose={handleEdgeDropMenuClose}
                />
            )}

        </>
    );
}

export default memo(WorkflowCanvas);
